
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArtItem, Language } from '../types';

interface HeroCarouselProps {
  arts: ArtItem[];
  language: Language;
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({ arts, language }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (arts.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % arts.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [arts]);

  if (arts.length === 0) return null;

  const isVideo = (url?: string) => {
    if (!url) return false;
    return url.toLowerCase().includes('.mp4') || url.includes('giphy.mp4');
  };

  const getVisibleItems = () => {
    const len = arts.length;
    if (len === 1) return [{ art: arts[0], position: 'center' }];
    const prevIndex = (index - 1 + len) % len;
    const nextIndex = (index + 1) % len;
    return [
      { art: arts[prevIndex], position: 'left' },
      { art: arts[index], position: 'center' },
      { art: arts[nextIndex], position: 'right' },
    ];
  };

  return (
    <div className="relative w-full max-w-[1400px] h-[400px] md:h-[700px] flex items-center justify-center py-20">
      <div className="relative w-full h-full flex items-center justify-center" style={{ perspective: '1200px' }}>
        <AnimatePresence initial={false} mode="popLayout">
          {getVisibleItems().map(({ art, position }) => {
            const isCenter = position === 'center';
            const isLeft = position === 'left';
            const isRight = position === 'right';

            return (
              <motion.div
                key={art.id}
                initial={{ 
                  opacity: 0,
                  scale: 0.6,
                  x: isLeft ? -500 : isRight ? 500 : 0,
                  rotateY: isLeft ? 45 : isRight ? -45 : 0,
                  zIndex: 0
                }}
                animate={{
                  opacity: isCenter ? 1 : 0.3,
                  scale: isCenter ? 1 : 0.7,
                  x: isCenter ? 0 : isLeft ? -380 : 380,
                  rotateY: isCenter ? 0 : isLeft ? 35 : -35,
                  zIndex: isCenter ? 30 : 10,
                }}
                exit={{ 
                  opacity: 0,
                  scale: 0.6,
                  x: isLeft ? -500 : isRight ? 500 : 0,
                  zIndex: 0
                }}
                transition={{
                  duration: 0.8,
                  ease: [0.16, 1, 0.3, 1]
                }}
                className="absolute w-[320px] md:w-[550px] h-full flex items-center justify-center pointer-events-none"
              >
                <div className="relative w-full h-full flex items-center justify-center">
                  <div className={`relative w-full h-full flex items-center justify-center transition-all duration-700 ${isCenter ? 'brightness-110' : 'brightness-50 grayscale-[1]'}`}>
                    <div className="relative w-full h-full flex items-center justify-center">
                      {isVideo(art.videoUrl || art.imageUrl) ? (
                        <video
                          src={art.videoUrl || art.imageUrl}
                          autoPlay
                          loop
                          muted
                          playsInline
                          preload="auto"
                          className={`max-w-full max-h-full object-contain ${isCenter ? 'drop-shadow-[0_35px_60px_rgba(0,0,0,1)]' : ''}`}
                        />
                      ) : (
                        <img
                          src={art.imageUrl}
                          alt={art.title[language]}
                          className={`max-w-full max-h-full object-contain ${isCenter ? 'drop-shadow-[0_35px_60px_rgba(0,0,0,1)]' : ''}`}
                        />
                      )}
                    </div>

                    {isCenter && (
                      <motion.div 
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="absolute bottom-6 left-0 right-0 z-20 flex flex-col items-center px-4 pointer-events-auto"
                      >
                        <h1 className="text-2xl md:text-5xl font-black text-white uppercase tracking-tighter leading-none oil-text drop-shadow-[0_10px_30px_rgba(0,0,0,1)] text-center">
                          {art.title[language]}
                        </h1>
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 z-50 flex gap-3">
        {arts.map((_, i) => (
          <button key={i} onClick={() => setIndex(i)} className="group py-4 px-1">
            <div className={`h-[1px] transition-all duration-700 rounded-full ${i === index ? 'bg-[#c084fc] w-12' : 'bg-white/5 w-6 group-hover:bg-white/20'}`} />
          </button>
        ))}
      </div>
    </div>
  );
};

export default HeroCarousel;
